// Load-hitch control: the blast-off through the saloon roof and every warp used to freeze the game
// while the next island was built, its shaders compiled and its buffers uploaded on one frame.
//
// - `budget` runs work in small slices at the top of each frame (generators and deferred jobs), so
//   an island can be built over a few hundred frames while the posse is still fighting.
// - `LightBudget` holds the number of point lights the renderer sees constant. Three.js bakes the
//   light count into every lit shader, so a gate light appearing (the world shown at blast-off, a
//   new island's doors, the boss's core) used to recompile every material in the scene.
// - `warmGroup` compiles a hidden group's shaders with KHR_parallel_shader_compile and uploads its
//   textures and buffers a few meshes at a time into a 1x1 target, so its first real frame is free.

import * as THREE from 'three';

type Step = Generator<unknown, void, unknown>;

class Budget {
  private tasks: Step[] = [];
  private jobs: { fn: () => void; at: string }[] = [];
  private lastRun = 0;
  private looping = false;
  /** ms of slice work allowed per frame */
  ms = 3;
  /** total ms spent and the longest single slice (the hitch probe reads these) */
  spent = 0;
  worstSlice = 0;
  /** where the running build last paused (set by the builders): names the worst slice */
  note = '';
  worstAt = '';
  /** every slice over 25 ms (the hitch probe prints them) */
  long: string[] = [];

  /** Run a generator to completion over many frames; each `yield` is a place it may pause. */
  add(gen: Step) { this.tasks.push(gen); this.kick(); }
  /** Run fn on a later frame inside the budget (heavy load callbacks: tree meshes, the train). */
  defer(fn: () => void, at = 'job') { this.jobs.push({ fn, at }); this.kick(); }
  drop(gen: Step) { const i = this.tasks.indexOf(gen); if (i >= 0) this.tasks.splice(i, 1); }
  get busy() { return this.tasks.length > 0 || this.jobs.length > 0; }

  /** Called by the game at the top of each frame. */
  run(ms = this.ms) {
    const t0 = performance.now();
    this.lastRun = t0;
    while (this.jobs.length || this.tasks.length) {
      const s = performance.now();
      const at = this.jobs.length ? this.jobs[0].at : this.note;
      const job = this.jobs.shift();
      try {
        if (job) job.fn();
        else if (this.tasks[0].next().done) this.tasks.shift();
      } catch (err) {
        // a broken load (a missing model) must not take the frame loop down with it
        if (!job) this.tasks.shift();
        console.warn(`[warm] ${at} failed`, err);
      }
      const e = performance.now();
      if (e - s > 25 && this.long.length < 40) this.long.push(`${(e - s).toFixed(0)}ms ${at}`);
      if (e - s > this.worstSlice) { this.worstSlice = e - s; this.worstAt = at; }
      if (e - t0 >= ms) break;
    }
    this.spent += performance.now() - t0;
  }

  /** Pages without a game loop calling run() (the labs) still get their work done. */
  private kick() {
    if (this.looping) return;
    this.looping = true;
    const tick = () => {
      if (performance.now() - this.lastRun > 60) this.run();
      if (this.busy) requestAnimationFrame(tick); else this.looping = false;
    };
    requestAnimationFrame(tick);
  }
}
export const budget = new Budget();

/** Step through a generator right now (the synchronous fallback). */
export function finish(gen: Step) { budget.drop(gen); for (;;) if (gen.next().done) return; }

// ------------------------------------------------------------------ constant point-light count

const WARM_LAYER = 31;

/**
 * Keeps exactly `count` point lights visible to the renderer by switching dark filler lights on and
 * off. Call `update(scene)` once per frame before rendering.
 */
export class LightBudget {
  private fill: THREE.PointLight[] = [];
  private seen = 0;
  constructor(readonly count = 4) {}

  update(scene: THREE.Scene) {
    let n = 0;
    scene.traverseVisible((o) => { if ((o as THREE.PointLight).isPointLight && !o.userData.fill) n++; });
    while (this.fill.length < this.count) {
      const l = new THREE.PointLight(0x000000, 0, 0.01, 2);
      l.userData.fill = true;
      l.position.set(0, -1e4, 0);
      l.layers.enableAll();
      scene.add(l);
      this.fill.push(l);
    }
    const need = Math.max(0, this.count - n);
    for (let i = 0; i < this.fill.length; i++) {
      this.fill[i].visible = i < need;
      if (this.fill[i].parent !== scene) scene.add(this.fill[i]);
    }
    if (n > this.count && n !== this.seen) console.warn(`[warm] ${n} point lights: shaders will recompile (budget ${this.count})`);
    this.seen = n;
  }
}

// ------------------------------------------------------------------ warming a hidden group

const lightsOf = (root: THREE.Object3D) => {
  const out: THREE.Light[] = [];
  root.traverse((o) => { if ((o as THREE.Light).isLight) out.push(o as THREE.Light); });
  return out;
};

const TEX_KEYS = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'emissiveMap', 'alphaMap', 'bumpMap', 'lightMap', 'gradientMap', 'specularMap', 'displacementMap'] as const;
function texturesOf(root: THREE.Object3D) {
  const out = new Set<THREE.Texture>();
  const add = (v: unknown) => { if (v && (v as THREE.Texture).isTexture && !(v as THREE.Texture & { isRenderTargetTexture?: boolean }).isRenderTargetTexture) out.add(v as THREE.Texture); };
  root.traverse((o) => {
    const ms = (o as THREE.Mesh).material;
    if (!ms) return;
    for (const m of Array.isArray(ms) ? ms : [ms]) {
      const r = m as unknown as Record<string, unknown>;
      for (const k of TEX_KEYS) add(r[k]);
      const u = (m as THREE.ShaderMaterial).uniforms;
      if (u) for (const k in u) add(u[k]?.value);
    }
  });
  return [...out];
}
const imageReady = (t: THREE.Texture) => {
  const im = t.image as { complete?: boolean; width?: number } | undefined;
  return t.version > 0 && !!im && (im.complete ?? true) && (im.width ?? 1) > 0;
};

let warmRT: THREE.WebGLRenderTarget | null = null;
const warmCam = new THREE.PerspectiveCamera();

/**
 * Compile, upload and draw `group` (which must NOT be in `scene`) so that showing it costs nothing.
 * The scene's own lights must be what they'll be once the group is shown: the group's own lights are
 * left out (LightBudget keeps the count).
 */
export function* warmGroup(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera, group: THREE.Object3D, bytesPerStep = 6e6): Step {
  warmRT ??= new THREE.WebGLRenderTarget(1, 1, { type: THREE.HalfFloatType, depthBuffer: true });
  const own = lightsOf(group);
  const hide = () => { const v = own.map((l) => l.visible); for (const l of own) l.visible = false; return () => own.forEach((l, i) => { l.visible = v[i]; }); };

  // 1. shaders: started all at once, finished by the driver on its own threads
  budget.note = 'warm:compile';
  {
    const restore = hide();
    const prev = renderer.getRenderTarget();
    renderer.setRenderTarget(warmRT); // programs are keyed on the target's output (the game draws into the post target)
    const p = renderer.compileAsync(group, camera, scene);
    renderer.setRenderTarget(prev);
    restore();
    let done = false;
    void p.then(() => { done = true; });
    const t0 = performance.now();
    while (!done && performance.now() - t0 < 20000) yield;
  }
  yield;

  // 2. textures: one upload per step
  budget.note = 'warm:textures';
  for (const t of texturesOf(group)) {
    if (!imageReady(t)) continue;
    renderer.initTexture(t);
    yield;
  }

  // 3. buffers (and the shadow depth shaders): draw a few meshes at a time into a 1x1 target, seen
  //    only by a camera on a layer nothing else in the scene uses
  const meshes: THREE.Mesh[] = [];
  group.traverse((o) => { const m = o as THREE.Mesh; if ((m.isMesh || (m as unknown as THREE.Points).isPoints || (m as unknown as THREE.Line).isLine) && m.geometry) meshes.push(m); });
  const sceneLights = lightsOf(scene);
  const bytes = (m: THREE.Mesh) => {
    let b = 0;
    const g = m.geometry;
    for (const k in g.attributes) b += (g.attributes[k] as THREE.BufferAttribute).array?.byteLength ?? 0;
    if (g.index) b += g.index.array.byteLength;
    return b;
  };
  let i = 0;
  while (i < meshes.length) {
    const batch: THREE.Mesh[] = [];
    let b = 0;
    while (i < meshes.length && (batch.length === 0 || b + bytes(meshes[i]) < bytesPerStep)) { b += bytes(meshes[i]); batch.push(meshes[i++]); }
    budget.note = `warm:draw ${batch.length} meshes ${(b / 1e6).toFixed(1)} MB (${batch[0].name})`;
    drawHidden(renderer, scene, camera, group, batch, sceneLights, hide);
    yield;
  }
}

function drawHidden(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera, group: THREE.Object3D, batch: THREE.Mesh[],
  sceneLights: THREE.Light[], hide: () => () => void) {
  const restoreLights = hide();
  const parent = group.parent;
  const groupVis = group.visible;
  scene.add(group);
  group.visible = true;
  // every ancestor of the batch has to be visible for the draw to reach it
  const vis: [THREE.Object3D, boolean][] = [];
  const saved: [THREE.Mesh, number, boolean][] = [];
  for (const m of batch) {
    saved.push([m, m.layers.mask, m.frustumCulled]);
    m.layers.set(WARM_LAYER); m.frustumCulled = false;
    for (let o: THREE.Object3D | null = m; o && o !== group; o = o.parent) if (!o.visible) { vis.push([o, false]); o.visible = true; }
  }
  const lightMasks = sceneLights.map((l) => l.layers.mask);
  for (const l of sceneLights) {
    l.layers.enable(WARM_LAYER);
    // run the shadow pass even where it's paused (the saloon), so the casters' depth shaders compile
    // too. It draws only this batch into the shadow map: the frame's own render redraws it after
    // (and aboard the car the shadow is faded out anyway)
    const sh = (l as THREE.DirectionalLight).shadow;
    if (l.castShadow && sh) sh.needsUpdate = true;
  }
  warmCam.copy(camera as THREE.PerspectiveCamera, false);
  warmCam.layers.set(WARM_LAYER);
  const prev = renderer.getRenderTarget();
  renderer.setRenderTarget(warmRT);
  renderer.render(scene, warmCam);
  renderer.setRenderTarget(prev);
  sceneLights.forEach((l, k) => { l.layers.mask = lightMasks[k]; });
  for (const [m, mask, fc] of saved) { m.layers.mask = mask; m.frustumCulled = fc; }
  for (const [o, v] of vis) o.visible = v;
  group.visible = groupVis;
  if (parent) parent.add(group); else scene.remove(group);
  restoreLights();
}

// ------------------------------------------------------------------ slicing a builder

/** A builder's pause point: `yield* pause('where')` (the WorldView build hands its own one down). */
export type Pause = (at: string) => Generator<unknown, void, unknown>;

/** Pauses a builder's loops whenever it has run `ms` since the last pause. */
export class Clock {
  private t = performance.now();
  constructor(private pause: Pause, private ms = 1.5) {}
  *tick(at: string): Generator<unknown, void, unknown> {
    if (performance.now() - this.t < this.ms) return;
    yield* this.pause(at);
    this.t = performance.now();
  }
}

// ------------------------------------------------------------------ textures that outlive an island

const shared = new Map<string, THREE.Texture>();
const keep = () => { /* shared: lives as long as the page */ };
/**
 * One texture per (url, setup key) for the whole session. Every island loads the same handful of
 * painted textures; loading them again on every warp decoded and uploaded them again on the frame
 * the new island first drew. Views still call dispose() on what they were given: on these it's a
 * no-op.
 */
export function sharedTexture(url: string, key = '', setup?: (t: THREE.Texture) => void): THREE.Texture {
  const k = url + '|' + key;
  let t = shared.get(k);
  if (!t) {
    t = new THREE.TextureLoader().load(url);
    setup?.(t);
    t.dispose = keep;
    shared.set(k, t);
  }
  return t;
}
/** Same, for a texture made in code (a gradient, an env map). */
export function sharedMade<T extends THREE.Texture>(key: string, make: () => T): T {
  let t = shared.get(key) as T | undefined;
  if (!t) { t = make(); t.dispose = keep; shared.set(key, t); }
  return t;
}
